'use client';
import { ApiError } from '@lib/api-error';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Listing } from 'types/collections';

import { ListingService } from '@services/ListingService';

export const useSchoolListing = () => {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error } = useQuery<Listing | null, ApiError>({
    queryKey: ['school-listing'],
    queryFn: () => new ListingService().getListing(),
  });

  const {
    data: listings,
    isPending: isPendingListings,
    isError: isErrorListings,
    error: errorListings,
  } = useQuery<Listing[] | null, ApiError>({
    queryKey: ['teacher-listing'],
    queryFn: () => new ListingService().getListings(),
  });

  const createListingMutation = useMutation({
    mutationFn: (listingData: {
      intro?: string;
      role?: string;
      rules?: string;
      supplies?: string;
    }) => new ListingService().createListing(listingData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-listing'] });
    },
  });

  const updateListingMutation = useMutation({
    mutationFn: (updates: {
      intro?: string;
      role?: string;
      rules?: string;
      supplies?: string;
      listing_completed?: boolean;
    }) => new ListingService().updateListing(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['school-listing'] });
    },
  });

  return {
    listing: data,
    isPending,
    isError,
    error,
    listings,
    isPendingListings,
    isErrorListings,
    errorListings,

    createListing: createListingMutation.mutateAsync,
    isCreating: createListingMutation.isPending,
    createError: createListingMutation.error,
    updateListing: updateListingMutation.mutateAsync,
    isUpdating: updateListingMutation.isPending,
    updateError: updateListingMutation.error,
  };
};

export const useSchoolListingById = (id: string) => {
  const {
    data: listingById,
    isPending,
    isError,
    error,
  } = useQuery<Listing, ApiError>({
    queryKey: ['teacher-listing', id],
    queryFn: () => new ListingService().getListingById(id),
  });
  return {
    listingById,
    isPending,
    isError,
    error,
  };
};
